// src/components/chat/BookmarkFavorites.jsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


// Define icon paths object outside the component for clarity
const iconPaths = {
  'bookmark': <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>,
  'star': <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>,
  'heart': <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>,
  'file-text': <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>,
  'message-square': <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>,
  'lightbulb': <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8A6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>,
  'check-circle': <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></>,
  'alert-circle': <><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></>,
};

// New SortableBookmarkItem component
const SortableBookmarkItem = ({ bookmark, onSelectBookmark }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bookmark.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  // Add console log here to inspect the icon name
  //console.log('SortableBookmarkItem icon name:', bookmark?.icon?.name);

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`bg-zinc-800/50 ${isDragging ? 'shadow-lg' : ''} hover:bg-zinc-700/50 p-3 rounded-lg cursor-pointer flex items-center space-x-2`}
      whileHover={{ scale: isDragging ? 1 : 1.02 }}
      whileTap={{ scale: isDragging ? 1 : 0.98 }}
      onClick={() => onSelectBookmark(bookmark)}
    >
       <div {...attributes} {...listeners} className="cursor-grab touch-none p-1 -ml-1 text-zinc-500 hover:text-zinc-300">
          <GripVertical size={16} />
       </div>
      <div className="flex items-center space-x-3 flex-grow">
        <div className={`p-2 rounded-full ${bookmark.icon.bgColor} text-white flex-shrink-0`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {/* Use object lookup for the icon path, fallback to bookmark icon if name not found */}
            {iconPaths[bookmark.icon.name]}
          </svg>
        </div>
        <div className="text-zinc-100 text-sm truncate">{bookmark.name}</div>
      </div>
    </motion.div>
  );
};


const BookmarkFavorites = ({
  isOpen,
  bookmarks,
  onSelectBookmark,
  onOpenManager,
  onReorderBookmarks
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  //console.log('bookmarks', bookmarks);

  // const handleDragEnd = (event) => {
  //   const { active, over } = event;

  //   if (active.id !== over.id) {
  //     const oldIndex = bookmarks.findIndex((bm) => bm.id === active.id);
  //     const newIndex = bookmarks.findIndex((bm) => bm.id === over.id);
  //     onReorderBookmarks(arrayMove(bookmarks, oldIndex, newIndex));
  //   }
  // };

  // const bookmarkIds = bookmarks.map(bm => bm.id);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="absolute bottom-16 -right-4 bg-zinc-900 rounded-lg shadow-xl border border-zinc-800 p-3 w-[400px] flex flex-col"
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 500 }}
        >
          <div className="flex items-center justify-between mb-3 px-2 flex-shrink-0">
            <h3 className="text-base font-normal text-zinc-200 ml-3">Favorites</h3>
            <button
              onClick={(e) => {
                e.preventDefault();
                onOpenManager();
              }}
              className="p-1.5 text-zinc-500 hover:text-sky-400 hover:bg-zinc-800 rounded-md"
              aria-label="Manage Bookmarks"
              type="button"
            >
              <span className="mr-1 text-xs">Manage</span><Settings size={16} className="inline-block" />
            </button>
          </div>
          {/* <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={bookmarkIds}
              strategy={verticalListSortingStrategy}
            > */}
              <div className="grid grid-cols-1 gap-2 overflow-x-hidden px-2 pb-1 overflow-y-auto flex-grow">
                {[...bookmarks] // Create a shallow copy to avoid mutating the original prop
                  .sort((a, b) => a.order - b.order) // Sort by the 'order' attribute
                  .map((bookmark) => (
                    <SortableBookmarkItem
                      key={bookmark.id}
                      bookmark={bookmark}
                      onSelectBookmark={onSelectBookmark}
                    />
                  ))}
                {bookmarks.length === 0 && (
                  <div className="text-zinc-500 text-sm text-center py-3">
                    No favorites yet - star a message to save a favorite
                  </div>
                )}
              </div>
            {/* </SortableContext>
          </DndContext> */}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookmarkFavorites;